import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/auth/ProtectedRoute";
import NotFound from "pages/NotFound";

// Public landing pages (rediseño jul-2026). Los componentes originales
// (./pages/landing, ./pages/landing/pricing, ./pages/landing/contact) se
// conservan en el repo por si se necesita revertir.
import Home2 from './pages/landing/Home2';
import Caracteristicas2 from './pages/landing/Caracteristicas2';
import Seguridad2 from './pages/landing/Seguridad2';
import Precios2 from './pages/landing/Precios2';
import Contacto2 from './pages/landing/Contacto2';
import Login2 from './pages/landing/Login2';
import Register2 from './pages/landing/Register2';

// App pages (auth-protected, under /app/*)
import CompanyDashboard from './pages/company-dashboard';
import EmployeePayrollPortal from './pages/employee-payroll-portal';
import CompanyReceipts from './pages/company-receipts';
import SatSyncScreen from './pages/sat-sync';
import SyncDetalle from './pages/sat-sync/detalle';
import SyncMasiva from './pages/sat-sync/masiva';
import CompanyManagement from './pages/company-management';
import SystemAnalytics from './pages/system-analytics';
import MandatoryPasswordChange from './pages/mandatory-password-change';
import CompanyFormPage from './pages/company-form';
import CompanyAdministratorManagement from './pages/company-administrator-management';
import SystemUserManagement from './pages/system-user-management';
import PayrollProcessing from './pages/payroll-processing';

// Account / billing
import AccountIndex from './pages/account';
import AccountProfile from './pages/account/profile';
import AccountPlan from './pages/account/plan';
import AccountUpgrade from './pages/account/upgrade';
import AccountBilling from './pages/account/billing';

// Admin billing
import AdminSubscriptions from './pages/admin-subscriptions';
import AdminCoupons from './pages/admin-coupons';

// Prospectos (asistente de WhatsApp)
import AdminProspects from './pages/admin-prospects';
import AdminProspectDetail from './pages/admin-prospects/detail';

// Magic-link (registro por asistente de WhatsApp)
import Entrar from './pages/entrar';

// Shell del rediseño (jul-2026): menú lateral + header contextual para /app/*
import AppLayout from './components/app2/AppLayout';
import RoleHome from './components/app2/RoleHome';

// Legal pages (public)
import PrivacyPolicy from './pages/legal/privacy-policy';
import TermsOfService from './pages/legal/terms-of-service';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* ============ Public Landing (rediseño) ============ */}
          <Route path="/" element={<Home2 />} />
          <Route path="/caracteristicas" element={<Caracteristicas2 />} />
          <Route path="/seguridad" element={<Seguridad2 />} />
          <Route path="/pricing" element={<Precios2 />} />
          <Route path="/contact" element={<Contacto2 />} />
          <Route path="/register" element={<Register2 />} />
          {/* Alias de preview del rediseño (mismo Home) */}
          <Route path="/landing2" element={<Home2 />} />

          {/* ============ Public Auth & Legal ============ */}
          <Route path="/login" element={<Login2 />} />
          <Route path="/entrar" element={<Entrar />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Mandatory password change - kept at root, available for all authenticated users */}
          <Route
            path="/mandatory-password-change"
            element={
              <ProtectedRoute allowedRoles={['administrator', 'company', 'employee']}>
                <MandatoryPasswordChange />
              </ProtectedRoute>
            }
          />

          {/* ============ App (protected, under /app/*) ============ */}
          {/* Ruta-layout: autentica una vez y monta el shell del rediseño
              (menú lateral + header). Cada pantalla conserva su propio
              ProtectedRoute con los roles que ya tenía. */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
          {/* /app manda a cada rol a su pantalla de inicio */}
          <Route path="/app" element={<RoleHome />} />

          {/* /app/administrator-dashboard quedó absorbido por Análisis del
              sistema (rediseño jul-2026). Renderizaba los MISMOS cuatro paneles
              con KPIs escritos a mano ("6 empresas activas") y una gestión de
              empresas inerte (lista vacía, formulario que no guardaba), y no era
              alcanzable desde ninguna navegación. Se conserva la ruta como
              redirección para no romper enlaces guardados. */}
          <Route path="/app/administrator-dashboard" element={<Navigate to="/app/system-analytics" replace />} />

          <Route
            path="/app/company-dashboard"
            element={
              <ProtectedRoute allowedRoles={['company', 'administrator']}>
                <CompanyDashboard />
              </ProtectedRoute>
            }
          />

          {/* Sincronizaciones: lo que corre ahora y el histórico auditable. El
              ciclo lo lleva el servidor, así que esta pantalla observa. */}
          <Route
            path="/app/sync"
            element={
              <ProtectedRoute allowedRoles={['company', 'administrator']}>
                <SatSyncScreen />
              </ProtectedRoute>
            }
          />

          {/* Varias empresas a la vez. Va ANTES de /app/sync/:id: si no, "masiva"
              entraría como identificador y caería en el detalle. */}
          <Route
            path="/app/sync/masiva"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <SyncMasiva />
              </ProtectedRoute>
            }
          />

          {/* El proceso exacto de UNA sincronización, con su bitácora. */}
          <Route
            path="/app/sync/:id"
            element={
              <ProtectedRoute allowedRoles={['company', 'administrator']}>
                <SyncDetalle />
              </ProtectedRoute>
            }
          />

          {/* Recibos de TODA la empresa, agrupados por periodo. El portal de
              autoservicio de abajo queda para el colaborador y para ver el
              detalle de una persona concreta desde el panel. */}
          <Route
            path="/app/receipts"
            element={
              <ProtectedRoute allowedRoles={['company', 'administrator']}>
                <CompanyReceipts />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/employee-payroll-portal"
            element={
              <ProtectedRoute allowedRoles={['employee', 'company', 'administrator']}>
                <EmployeePayrollPortal />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/company-form"
            element={
              <ProtectedRoute allowedRoles={['administrator', 'company']}>
                <CompanyFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/company-management"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <CompanyManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/company-administrator-management"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <CompanyAdministratorManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/system-analytics"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <SystemAnalytics />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/system-user-management"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <SystemUserManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/payroll-processing"
            element={
              <ProtectedRoute allowedRoles={['company', 'administrator']}>
                <PayrollProcessing />
              </ProtectedRoute>
            }
          />

          {/* /app/user-profile-management quedó consolidado en Mi perfil
              (rediseño jul-2026): montaba exactamente los mismos componentes que
              /app/account/profile. Se conserva como redirección para no romper
              enlaces guardados. */}
          <Route path="/app/user-profile-management" element={<Navigate to="/app/account/profile" replace />} />

          {/* ============ Mi cuenta (billing/profile, mostly company_admin) ============ */}
          <Route
            path="/app/account"
            element={
              <ProtectedRoute allowedRoles={['administrator', 'company', 'employee']}>
                <AccountIndex />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/account/profile"
            element={
              <ProtectedRoute allowedRoles={['administrator', 'company', 'employee']}>
                <AccountProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/account/plan"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <AccountPlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/account/plans/upgrade"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <AccountUpgrade />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/account/billing"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <AccountBilling />
              </ProtectedRoute>
            }
          />

          {/* ============ Prospectos (super_admin) ============ */}
          <Route
            path="/app/admin/prospects"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <AdminProspects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/admin/prospects/:id"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <AdminProspectDetail />
              </ProtectedRoute>
            }
          />

          {/* ============ Admin billing (super_admin) ============ */}
          <Route
            path="/app/admin/subscriptions"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <AdminSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/admin/coupons"
            element={
              <ProtectedRoute allowedRoles={['administrator']}>
                <AdminCoupons />
              </ProtectedRoute>
            }
          />
          </Route>
          {/* ── fin de la ruta-layout /app/* ── */}

          {/* ============ Legacy redirects (old top-level → /app/*) ============ */}
          {/* Preserve any bookmarked URLs during the transition. */}
          <Route path="/administrator-dashboard" element={<Navigate to="/app/administrator-dashboard" replace />} />
          <Route path="/company-dashboard" element={<Navigate to="/app/company-dashboard" replace />} />
          <Route path="/employee-payroll-portal" element={<Navigate to="/app/employee-payroll-portal" replace />} />
          <Route path="/company-form" element={<Navigate to="/app/company-form" replace />} />
          <Route path="/company-management" element={<Navigate to="/app/company-management" replace />} />
          <Route path="/company-administrator-management" element={<Navigate to="/app/company-administrator-management" replace />} />
          <Route path="/system-analytics" element={<Navigate to="/app/system-analytics" replace />} />
          <Route path="/system-user-management" element={<Navigate to="/app/system-user-management" replace />} />
          <Route path="/payroll-processing" element={<Navigate to="/app/payroll-processing" replace />} />
          <Route path="/user-profile-management" element={<Navigate to="/app/user-profile-management" replace />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
